/*
 * This file gets bundled and placed in `/build/assets` along with it's assets
 */
import 'react-dates/initialize'
import './rootStyles'

import { startSpan } from '@sentry/browser'
import queryString from 'query-string'
import { createRoot } from 'react-dom/client'

import renderApp from '@app/renderers/main'
import type { ServerContext } from '@app/renderers/shell'
import type { AnalyticsUser } from '@app/types/analytics'

import { CLIENT_ERROR_CODE } from '@app/constants/Misc'

import { AbortControllerService } from '@app/services/AbortControllerService'
import { Amplitude } from '@app/services/Amplitude'
import { createAnalyticsRouter } from '@app/services/analyticsRouter'
import { analyticsIdentify } from '@app/services/analyticsUser'
import { headers as apiHeaders } from '@app/services/apiHeaders'
import { ApiInterceptor } from '@app/services/ApiInterceptor'
import { ApplicationAgent } from '@app/services/ApplicationAgent'
import { AuthSessionManager, SESSION_SKEW_FRACTION } from '@app/services/AuthSessionManager'
import { BroadcastService } from '@app/services/BroadcastService'
import { ChatLoader } from '@app/services/ChatLoader'
import { CookieManager } from '@app/services/CookieManager'
import { DataRefreshService } from '@app/services/DataRefreshService'
import { DOMService } from '@app/services/DOMService'
import { ErrorReportController } from '@app/services/ErrorReportController'
import { FacebookLoader } from '@app/services/FacebookLoader'
import { getFetch, setFetch } from '@app/services/fetch'
import { FirebaseLoader } from '@app/services/FirebaseLoader'
import { IconsCache } from '@app/services/IconsCache'
import { IntlChunkLoader } from '@app/services/IntlChunkLoader'
import { IntlService } from '@app/services/IntlService'
import { KeyboardNavigation } from '@app/services/KeyboardNavigation'
import { Progress } from '@app/services/Progress/Progress'
import { PushNotificationsManager } from '@app/services/PushNotificationsManager'
import { StoreSerializer } from '@app/services/StoreSerializer'
import { SupervisedAuthSession } from '@app/services/SupervisedAuthSession'
import { TitleService } from '@app/services/TitleService'
import { TrackingsService } from '@app/services/TrackingsService'
import { WindowFocusTracker } from '@app/services/WindowFocusTracker'

import { clearSession, extractSession, readAccessTokenMeta, writeAccessTokenCookieFromClient } from '@app/utils/authCookies'
import { decideBfcacheAuthAction } from '@app/utils/bfcacheAuthReconcile'
import { BroadcastSessionAction, decideBroadcastSessionAction } from '@app/utils/broadcastSessionReconcile'
import { Cache } from '@app/utils/cache'
import { correctedNow, setClockOffset } from '@app/utils/clockOffset'
import { BrowserCookieStore } from '@app/utils/cookies'
import { getDataLayerPush } from '@app/utils/dataLayer'
import { getOrCreateDeviceId } from '@app/utils/deviceId'
import { installChunkLoadErrorReload } from '@app/utils/errorReport/chunkLoadErrorReload'
import * as errorReport from '@app/utils/errorReport/errorReport'
import { init as sentryInit } from '@app/utils/errorReport/errorReportInitClient'
import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import { invariant } from '@app/utils/invariant'
import { snapshotFromJwt } from '@app/utils/jwt'
import { loadScript } from '@app/utils/loadScript'
import { RouteDataExtractor } from '@app/utils/RouteDataExtractor'
import { setRouter } from '@app/utils/routing/Context'
import { RouterContext } from '@app/utils/routing/RouterContext'
import { hasSessionExpired, stripSessionExpired, withSessionExpired } from '@app/utils/sessionExpiredParam'
import { sleep } from '@app/utils/sleep'
import { tryOr } from '@app/utils/tryOr'

import { Context } from '@app/packages/actionContext/Context'
import { analyticsEvent, analyticsPageview } from '@app/packages/analytics/declaration'
import { ApiDispatcher } from '@app/packages/api/ApiDispatcher'
import { asError } from '@app/packages/asError/asError'
import { withDisposableStack } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'
import { ApiDispatcher as InternalApiDispatcher } from '@app/packages/internalApi/ApiDispatcher'
import { BrowserHistory } from '@app/packages/routing/BrowserHistory'
import { createBrowserRouter } from '@app/packages/routing/BrowserRouter'
import type { History, RouterContextInterface, RouterInterfaceAfterRenderCallback } from '@app/packages/routing/types'
import { resolveThunkSelector, unwrapLoadableState } from '@app/packages/thunkSelector/thunkSelector'

import { getAnalyticsRouter } from '@app/store/actions/analytics'
import { canAskAppForSessionToken, requestAppSessionToken } from '@app/store/actions/appSessionToken'
import { setAbortControllerService } from '@app/store/actions/contexts/abortControllerService'
import { setAmplitude } from '@app/store/actions/contexts/Amplitude'
import { setAnalyticsRouter } from '@app/store/actions/contexts/AnalyticsRouter'
import { setApiDispatcher } from '@app/store/actions/contexts/apiDispatcher'
import { getApiInterceptor, setApiInterceptor } from '@app/store/actions/contexts/apiInterceptor'
import { setApplicationAgent } from '@app/store/actions/contexts/applicationAgent'
import { getApplicationBridge, setApplicationBridge } from '@app/store/actions/contexts/applicationBridge'
import { getAuthSession, setAuthSession } from '@app/store/actions/contexts/authSession'
import { setBroadcastService, unsetBroadcastService } from '@app/store/actions/contexts/broadcastService'
import { getCookies, setCookies } from '@app/store/actions/contexts/cookies'
import { setErrorReportController } from '@app/store/actions/contexts/errorReportController'
import { setIconsCache } from '@app/store/actions/contexts/iconCache'
import { setIconContext } from '@app/store/actions/contexts/IconContext'
import { setInternalApiDispatcher } from '@app/store/actions/contexts/internalApiDispatcher'
import { setIntlService } from '@app/store/actions/contexts/intlService'
import { setKeyboardNavigation } from '@app/store/actions/contexts/keyboardNavigation'
import { setProgress } from '@app/store/actions/contexts/progress'
import { setPushNotificationsManager } from '@app/store/actions/contexts/pushNotificationsManager'
import { setLocale } from '@app/store/actions/initial'
import { beforeNavigate, navigate } from '@app/store/actions/router'
import { clearSupervision, teardownLostSession } from '@app/store/actions/session'
import { ActionTrail } from '@app/store/actionTrail'
import { BASE_REDUX_SLICE_CONTAINER } from '@app/store/redux_slice'
import { configSelector } from '@app/store/slices/config'
import { getCookieState, hasCookieConsentSelector, restoreConsent } from '@app/store/slices/cookies'
import { raiseRouterErrorSlice } from '@app/store/slices/error'
import { localeSelector } from '@app/store/slices/locale'
import { metaSelector } from '@app/store/slices/meta'
import {
  applyPendingPromocode,
  clearPendingPromocode,
  pendingPromocodeToApplySelector,
  pendingPromocodeTooLateSelector,
} from '@app/store/slices/pendingPromocode'
import { profileUserSelector } from '@app/store/slices/profile'
import { profileSuspendedSelector } from '@app/store/slices/profile_suspend'
import { getScriptsLoaded, processScripts } from '@app/store/slices/scripts'
import {
  effectiveAccessTokenSelector,
  isSupervisedSelector,
  setV3SessionSlice,
  supervisedAccessTokenSelector,
  v3SessionSelector,
} from '@app/store/slices/session'
import { getWasRefreshedDuringSsr, setWasRefreshedDuringSsr } from '@app/store/slices/ssrAuth'
import { testFlightsResultSelector } from '@app/store/slices/test_flights'
import type { Store } from '@app/store/store'
import createStore, { storeMonitoring, waitForMappedValue } from '@app/store/store'

import { useIcon } from '@app/components/Icon/useIconClient'
import { getMount } from '@app/components/Mount/actions'

export class ClientRunner {
  store!: Store
  rootNode!: HTMLElement
  storeContext!: Context
  history!: History
  routerContext!: RouterContext

  private initial = false
  private browserTimestamp: number = (window as any).BROWSER_TIMESTAMP
  private amplitude = new Amplitude()
  private intlChunkLoader: IntlChunkLoader | null = null

  async load(ctx: ServerContext) {
    const stack = new DisposableStack()

    try {
      await this.configureProxy()
      const initialState = new StoreSerializer().deserialize(ctx.storeState ?? invariant('Initial state is missing'))
      const actionTrail = new ActionTrail()
      sentryInit({ requestId: configSelector(initialState).appRequestId, errorReportController: this.errorReporter, getLastActions: () => actionTrail.read() })
      installChunkLoadErrorReload()

      this.rootNode = document.getElementById('Kidsout')!

      this.storeContext = new Context()

      this.history = new BrowserHistory()
      const mutator = configSelector(initialState).isPolygon ? await import('@app/packages/polygon/helpers').then(m => m.reducerWithMutator) : undefined
      this.store = createStore(this.storeContext, [BASE_REDUX_SLICE_CONTAINER], initialState, mutator, [actionTrail.middleware])
      this.store.dispatch(setFetch(global.fetch))

      // Correct for a wrong device clock: offset "now" by (server render time − browser parse time).
      // Both are wall clocks captured in the shell — SERVER_TIMESTAMP at render, BROWSER_TIMESTAMP at HTML parse.
      const serverTs = (window as any).SERVER_TIMESTAMP
      const browserTs = (window as any).BROWSER_TIMESTAMP
      if (typeof serverTs === 'number' && typeof browserTs === 'number') {
        setClockOffset(serverTs - browserTs)
      }

      this.configureApplicationAgent()

      this.store.dispatch(setIconContext({ useIcon }))
      this.store.dispatch(setProgress(new Progress()))
      this.store.dispatch(setIntlService(this.getIntlService(ctx)))
      this.store.dispatch(setApiInterceptor(new ApiInterceptor()))
      this.store.dispatch(setErrorReportController(this.errorReporter))
      this.store.dispatch(setAbortControllerService(new AbortControllerService()))
      this.store.dispatch(setIconsCache(new IconsCache().restore(ctx.iconsCache ?? invariant('Icons cache is missing'))))
      this.store.dispatch(setKeyboardNavigation(new KeyboardNavigation()))
      // Ahead of the auth manager: a session lost during boot logs out through it. `logout` before `init` is a no-op.
      this.store.dispatch(setAmplitude(this.amplitude))

      const broadcastService = this.configureBroadcastService(stack)
      stack.use(this.configureAuthSessionAndDispatcher(broadcastService))

      const internalApiDispatcher = this.configureInternalApiDispatcher(this.store.dispatch(getFetch()))
      await this.configureCookies(internalApiDispatcher)

      this.parseUtmCookie()
      await this.configureIntlService()
      stack.use(this.configureIntlChunkLoader())
      await this.configureApiInterceptor()
      this.sendOriginalLocationDataLayer()
      stack.use(this.configureApplicationBridge())
      stack.use(this.configureTitleService())
      stack.use(this.configureDOMService())
      stack.use(this.configureDataRefresh())
      stack.use(this.configurePendingPromocode())
      this.configureDebug()
      await this.configureAmplitude()
      this.configureAnalytics()
      stack.use(new FirebaseLoader(this.store))
      stack.use(new ChatLoader(this.store))
      this.store.dispatch(setPushNotificationsManager(new PushNotificationsManager(this.store)))
      stack.use(this.configureThirdPartyScripts())
    } catch (e) {
      stack.dispose()
      throw e
    }

    return stack
  }

  async run() {
    if (this.initial) {
      this.initial = false
    } else {
      this.browserTimestamp = new Date().getTime()
    }

    let initial = true

    const routesSelector = (
      await (POLYGON_ENABLED && configSelector(this.store.getState()).isPolygon
        ? import('@app/packages/polygon/routes_setup')
        : import('@app/routes_list/routes_list'))
    ).routesSelector

    try {
      let currentLocation = this.history.location

      const component = renderApp({
        router: createBrowserRouter({
          history: this.history,
          context: RouterContext.create(this.store),
          onRouterReady: this.handleOnRouterReady,
          // BuildTracker flags this once prod ships a new sha. Long-lived tabs kept routing on the
          // old bundle and calling endpoints only that build knew about — retired ones weeks later.
          shouldHardNavigate: () => configSelector(this.store.getState()).build.needsReload,
          onNavigation: async (location, action, p) => {
            await startSpan({ name: `${action} ${location.pathname}`, op: 'navigation' }, async _span => {
              const previous = currentLocation
              currentLocation = location
              // A soft replace that keeps the path is the page writing its own state into the URL, as the search map does
              // after every move; it must not close the modal opened over it.
              const inPlace = action === 'REPLACE' && location.state?.hard === false && location.pathname === previous.pathname
              const leaving = previous.key !== location.key && !inPlace
              await sleep(10)
              this.store.dispatch(beforeNavigate({ initial, location, action }))
              if (leaving) {
                await this.store.dispatch(getMount())?.autoclose()
              }
              await p
              await this.intlChunkLoader?.flushNow()
              this.store.dispatch(navigate({ location, action }))
              initial = false
            })
          },
        }),
        routesSelector,
        history: this.history,
        store: this.store,
        afterRender: this.handleAfterRender,
      })
      const root = createRoot(this.rootNode, {})
      root.render(component)
      // A public page renders where the reader was; the marker has done its job and leaves the address bar.
      if (hasSessionExpired(window.location.search)) {
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${stripSessionExpired(window.location.search)}${window.location.hash}`
        )
      }
      await waitIdle()
    } catch (error) {
      this.handleError(error)
    }
  }

  errorReporter = new ErrorReportController()

  private handleError = (e: unknown) => {
    this.errorReporter.report(e)

    const error = e ? asError(e) : null
    const errorCode = error && 'status' in error && typeof error.status === 'number' ? error.status : CLIENT_ERROR_CODE
    const message = error?.message
    const localizedMessage = error && 'localizedMessage' in error && typeof error.localizedMessage === 'string' ? error.localizedMessage : message
    this.store.dispatch(raiseRouterErrorSlice({ errorCode, message, localizedMessage }))
  }

  private async configureCookies(internalApiDispatcher: InternalApiDispatcher) {
    const cookies = new CookieManager(new BrowserCookieStore({ apiDispatcher: internalApiDispatcher }))
    cookies.restoreState(this.store.dispatch(getCookieState()) ?? {})
    this.store.dispatch(setCookies(cookies))
    await this.store.dispatch(restoreConsent())
  }

  private configureApplicationAgent() {
    this.store.dispatch(setApplicationAgent(new ApplicationAgent(configSelector(this.store.getState()).userAgentData!)))
  }

  private async configureApiInterceptor() {
    if (!configSelector(this.store.getState()).isPolygon) return
    const apiInterceptor = this.store.dispatch(getApiInterceptor())
    const { ApiInterceptor } = await import('@app/packages/polygon/ApiInterceptor')
    const polygonInterceptor = new ApiInterceptor(apiInterceptor)
    polygonInterceptor.interceptInitial()
  }

  private async configureProxy() {
    const proxyParam = queryString.parse(window.location.search).proxy
    const proxySwitcherKey = 'kidsout__proxy-enabled'

    /**
     * Due to a bug in early ios versions, attempting to extend built-in object resolves in TypeError,
     * so we disabling proxying as it using such technique (see ../utils/proxy module)
     *
     * https://stackoverflow.com/questions/26044056/typeerror-attempted-to-assign-to-readonly-property-in-angularjs-application-on
     * https://github.com/mozilla/pdf.js/issues/5353
     */
    const isWebSocketOverridePossible = /OS (8|9)_.*? like Mac OS X/.test(window.navigator.userAgent) === false

    if (typeof proxyParam === 'string') {
      localStorage?.setItem(proxySwitcherKey, proxyParam === 'on' ? 'true' : 'false')
    }
    const proxyEnabled = isWebSocketOverridePossible && localStorage?.getItem(proxySwitcherKey) === 'true'

    if (proxyEnabled) {
      const proxy = await import('@app/utils/proxy')
      proxy.enable()
    }
  }

  private configureThirdPartyScripts() {
    return spawn(async stack => {
      const ab = stack.adopt(new AbortController(), ab => ab.abort())
      if (configSelector(this.store.getState()).isApp || configSelector(this.store.getState()).isPolygon) return

      /** delay third-party scripts loading so they don't decrease pagespeed score */
      const sleepPromise = sleep(10000, ab.signal)
      const consentPromise = waitForMappedValue(
        this.store,
        hasCookieConsentSelector,
        hasConsent => {
          if (hasConsent.error) return null
          if (unwrapLoadableState(hasConsent.value)) return { val: true }
          return null
        },
        ab.signal
      )

      await Promise.all([sleepPromise, consentPromise])

      const scriptsLoaded = this.store.dispatch(getScriptsLoaded())
      if (!scriptsLoaded) {
        await this.store.dispatch(
          processScripts(script => {
            const range = document.createRange()
            range.selectNode(document.body)
            const fragment = range.createContextualFragment([script.head, script.body].join('\n'))
            document.body.appendChild(fragment)
          })
        )
      }

      ab.signal.throwIfAborted()

      FacebookLoader.shared.load().catch(e => {
        console.error(e.message)
      })

      void sleepPromise.then(() => {
        if (window.VK) return
        loadScript('//vk.com/js/api/openapi.js').catch(e => {
          console.error(e.message)
        })
      })
    })
  }

  private configureBroadcastService(stack: DisposableStack): BroadcastService {
    const service = stack.use(new BroadcastService())
    let pendingReload: BroadcastSessionAction.Logout | BroadcastSessionAction.SessionChange | null = null
    const performReload = async (kind: BroadcastSessionAction.Logout | BroadcastSessionAction.SessionChange) => {
      if (kind === BroadcastSessionAction.Logout) {
        const cookies = this.store.dispatch(getCookies())
        // Await the clear before reload — reloading mid-request would abort the
        // cookie-delete and leave the legacy session cookie behind.
        await clearSession(cookies).catch(() => {})
      }
      window.location.reload()
    }
    const onVisibilityChange = () => {
      if (pendingReload && document.visibilityState === 'visible') {
        const kind = pendingReload
        pendingReload = null
        void performReload(kind)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    stack.defer(() => document.removeEventListener('visibilitychange', onVisibilityChange))

    // A page restored from the bfcache resumes with frozen in-memory auth and has
    // missed every BroadcastChannel session-sync message sent while it was frozen.
    // Reconcile against the durable kidsout__at_meta cookie before any request fires.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      const action = decideBfcacheAuthAction(v3SessionSelector(this.store.getState()) ?? null, readAccessTokenMeta(), correctedNow())
      if (action === 'reload-logout') {
        void performReload(BroadcastSessionAction.Logout)
      } else if (action === 'reload-session-change') {
        void performReload(BroadcastSessionAction.SessionChange)
      } else if (action === 'invalidate-token') {
        this.store.dispatch(getAuthSession()).invalidateAccessToken()
      }
    }
    window.addEventListener('pageshow', onPageShow)
    stack.defer(() => window.removeEventListener('pageshow', onPageShow))
    stack.use(
      service.addListener(message => {
        if (message.type !== 'auth.session_refreshed') return
        const state = this.store.getState()
        // The session's subject, not the profile's: the profile slice fills asynchronously
        // and reading `null` from it turns an ordinary rotation into an apparent account
        // switch — a reload for nothing.
        const currentUserId = v3SessionSelector(state)?.user_id ?? profileUserSelector(state)?.id ?? null
        const kind = decideBroadcastSessionAction(message.auth, currentUserId)
        if (kind === BroadcastSessionAction.None) return // pure rotation — manager handler absorbs
        if (document.visibilityState === 'visible') {
          void performReload(kind)
        } else {
          pendingReload = kind
        }
      })
    )
    this.store.dispatch(setBroadcastService(service))
    stack.defer(() => {
      this.store.dispatch(unsetBroadcastService())
    })
    return service
  }

  private configureAuthSessionAndDispatcher(broadcastService: BroadcastService) {
    return spawn(stack => {
      const initialSnapshot = v3SessionSelector(this.store.getState()) ?? null
      // Read once: the app id and its protocol come from the user agent, and do not change under
      // a page.
      const appConfig = configSelector(this.store.getState())

      const manager = new AuthSessionManager({
        refreshUrl: '/api/v3/auth/refresh',
        exchangeUrl: '/api/v3/auth/exchange',
        fetch: this.store.dispatch(getFetch()),
        initialSnapshot,
        hasRefreshSession: () => {
          // In the native app webview the refresh token lives in the app, not as
          // a kidsout_rt cookie here — the app pushes fresh access tokens via the
          // `token_refresh` bridge message. So the web must never refresh itself.
          if (configSelector(this.store.getState()).isApp) return false
          return !!effectiveAccessTokenSelector(this.store.getState())
        },
        getLegacyExchangeParams: async () => {
          const cookies = this.store.dispatch(getCookies())
          const legacy = await extractSession(cookies)
          if (!legacy?.access_token) return null
          return {
            access_token: legacy.access_token,
            client_id: 'web',
            client_type: 'web',
            device_id: await getOrCreateDeviceId(cookies),
          }
        },
        // A stale token in the app's webview is not a lost session — the refresh material is in
        // the app. Installed only where the app can answer: below 3.46.0 nothing replies, and the
        // manager's own teardown is still the best available answer there.
        requestExternalToken: canAskAppForSessionToken(appConfig.appProtocol, appConfig.isApp)
          ? () => this.store.dispatch(requestAppSessionToken())
          : undefined,
        onAuthLost: () => {
          // The manager already broadcasts auth.session_refreshed({ auth: null })
          // before invoking this callback, so peer tabs are notified. Reload
          // only after the cookie clears settle — reloading mid-request would
          // abort them and leave the dead session cookies behind.
          void this.store.dispatch(teardownLostSession()).finally(() => {
            // In the app's WebView the app owns auth and decides what follows; elsewhere the server
            // reads the marker and sends a route that needs sign-in to /login with the reason.
            if (appConfig.isApp) window.location.reload()
            else window.location.replace(withSessionExpired(window.location.href))
          })
        },
        onSessionRefreshed: snapshot => {
          this.store.dispatch(setV3SessionSlice(snapshot))
          void this.store.dispatch(writeAccessTokenCookieFromClient(snapshot))
        },
        broadcast: {
          addListener: handler =>
            broadcastService.addListener(message => {
              if (message.type === 'auth.session_refreshed') {
                handler(message.auth)
              }
            }),
          send: snapshot => broadcastService.send({ type: 'auth.session_refreshed', auth: snapshot }),
        },
        getExtraHeaders: () => this.store.dispatch(apiHeaders()),
        now: correctedNow,
        // Refresh within the last fraction of the token's real lifetime rather
        // than a fixed lead, so the cadence follows whatever expiry the backend
        // issues — a short-lived token no longer reads as perpetually "within
        // skew" (which would refresh on every request). For long tokens this
        // still leaves a wide pre-expiry buffer, covering iOS Safari suspending
        // background-tab timers so a resumed tab isn't left with a stale token.
        skewFraction: SESSION_SKEW_FRACTION,
      })
      stack.use(manager)

      const authSession = new SupervisedAuthSession({
        inner: manager,
        getSupervisionToken: () => supervisedAccessTokenSelector(this.store.getState()),
        onSupervisionExpired: () => {
          // Exit supervision (clears state, then the httpOnly cookie) and reload only
          // after the cookie clear settles, so the stale cookie isn't resent on load.
          void this.store.dispatch(clearSupervision()).finally(() => {
            window.location.reload()
          })
        },
        now: correctedNow,
      })
      this.store.dispatch(setAuthSession(authSession))

      this.store.dispatch(
        setApiDispatcher(
          new ApiDispatcher({
            base: '',
            fetch: this.store.dispatch(getFetch()),
            getHeaders: options => this.store.dispatch(apiHeaders(options)),
            authSession,
          })
        )
      )

      if (this.store.dispatch(getWasRefreshedDuringSsr())) {
        // Forward the SSR-determined auth snapshot to peer tabs. null when SSR
        // detected auth loss (peer tabs treat as logout); otherwise the
        // freshly-minted snapshot (peers absorb, or reload if user_id differs).
        broadcastService.send({ type: 'auth.session_refreshed', auth: initialSnapshot })
        this.store.dispatch(setWasRefreshedDuringSsr(false))
      }
    })
  }

  private getIntlService(ctx: ServerContext) {
    const translationsCache = new Cache<{ [key: string]: string }>(
      new Map(Object.entries(ctx.translations ?? invariant('Translations are missing')).map(([key, data]) => [key, { data }]))
    )

    return new IntlService(async locale => {
      if (translationsCache.has(locale)) return translationsCache.get(locale)
      const resp = await fetch(`/internal/translations/${locale}`)
      if (resp.status !== 200) throw new Error('Failed to fetch translations')
      return resp.json()
    })
  }

  private async configureIntlService() {
    const state = this.store.getState()
    await this.store.dispatch(setLocale(localeSelector(state)))
  }

  private configureIntlChunkLoader() {
    this.intlChunkLoader = new IntlChunkLoader(this.store, localeSelector(this.store.getState()))
    return this.intlChunkLoader.start()
  }

  private configureTitleService() {
    return new TitleService(this.store)
  }

  private configureDOMService() {
    return new DOMService(this.store)
  }

  private configureInternalApiDispatcher(fetch: typeof globalThis.fetch) {
    const internalApiDispatcher = new InternalApiDispatcher({
      base: '',
      fetch,
      getHeaders: () => this.store.dispatch(apiHeaders()),
      authSession: this.store.dispatch(getAuthSession()),
    })
    this.store.dispatch(setInternalApiDispatcher(internalApiDispatcher))
    return internalApiDispatcher
  }

  private configureDataRefresh() {
    return spawn(async stack => {
      if (configSelector(this.store.getState()).isPolygon) return
      const dataRefreshService = new DataRefreshService(this.store)
      let stop: DisposableStack | null = null
      stack.defer(() => {
        stop?.dispose()
        stop = null
      })
      const focusTracker = stack.use(new WindowFocusTracker())
      stack.use(
        focusTracker.isFocused.sync(isFocused => {
          if (isFocused && !stop) {
            stop = dataRefreshService.start()
          } else if (!isFocused && stop) {
            stop.dispose()
            stop = null
          }
        })
      )
    }, reportSpawnFailure('DataRefresh'))
  }

  /** A /promocode?code= link's code waits in the store until the visitor can take it, or turns out unable to. */
  private configurePendingPromocode() {
    return withDisposableStack(stack => {
      stack.use(
        storeMonitoring(this.store, pendingPromocodeToApplySelector, code => {
          if (code) void this.store.dispatch(applyPendingPromocode(code))
        })
      )
      stack.use(
        storeMonitoring(this.store, pendingPromocodeTooLateSelector, code => {
          if (code) void this.store.dispatch(clearPendingPromocode())
        })
      )
    })
  }

  private configureApplicationBridge() {
    return spawn(async stack => {
      if (!configSelector(this.store.getState()).isApp) return
      const { ApplicationBridge } = await import('@app/services/ApplicationBridge')
      const applicationBridge = new ApplicationBridge(configSelector(this.store.getState()).applicationAgentData ?? null, this.store)
      applicationBridge.registerIncomingHandler()
      if (applicationBridge?.canSendAction()) {
        this.history.intercept(async event => {
          const result = await applicationBridge.sendAction('navigation', {
            pathname: event.action.location.pathname,
            search: event.action.location.search ?? '',
            hash: event.action.location.hash ?? '',
            action: event.action.type,
          })
          if (result.handled) event.preventDefault()
        })
      }
      this.store.dispatch(setApplicationBridge(applicationBridge))

      // The app pushes fresh access tokens (e.g. ahead of expiry) so the webview
      // can keep authenticating without a refresh session of its own. Seed the
      // snapshot into the auth manager — it updates the slice, cookie and peers.
      stack.defer(
        applicationBridge.addMessageListener(message => {
          if (message.type !== 'token_refresh') return
          const snapshot = snapshotFromJwt(message.access_token)
          if (!snapshot) {
            errorReport.captureException(new Error('token_refresh: access token is not a usable JWT'))
            return
          }
          this.store.dispatch(getAuthSession()).adoptSnapshot(snapshot)
        })
      )

      // The app's suspension screen closes on this message, so it follows what the page itself shows.
      let wasSuspended: boolean | null = null
      stack.use(
        storeMonitoring(this.store, profileSuspendedSelector, suspended => {
          if (suspended === null) return
          if (wasSuspended && !suspended) applicationBridge.sendMessage({ type: 'suspend_resolved' })
          wasSuspended = suspended
        })
      )
    }, reportSpawnFailure('ApplicationBridge'))
  }

  private configureDebug() {
    if (API_MODE !== 'production') {
      ;(window as any).ReduxStore = this.store
    }
  }

  private parseUtmCookie() {
    const params_array = window.location.search.substring(1).split('&')
    const params_result = {}
    for (let i = 0; i < params_array.length; i++) {
      const params_current = params_array[i].split('=')
      params_result[params_current[0]] = typeof params_current[1] === 'undefined' ? '' : params_current[1]
    }
    if (params_result['utm_source']) {
      const date = new Date()
      const postClick = 30
      date.setDate(date.getDate() + postClick)
      document.cookie = 'utm_source=' + params_result['utm_source'] + ';expires=' + date
      document.cookie = 'utm_medium=' + params_result['utm_medium'] + ';expires=' + date
      document.cookie = 'utm_campaign=' + params_result['utm_campaign'] + ';expires=' + date
      document.cookie = 'utm_content=' + params_result['utm_content'] + ';expires=' + date
      document.cookie = 'utm_term=' + params_result['utm_term'] + ';expires=' + date
    }
  }

  private sendOriginalLocationDataLayer() {
    const params_array = window.location.search.substring(1).split('&')
    const params_result = {}
    for (let i = 0; i < params_array.length; i++) {
      const params_current = params_array[i].split('=')
      params_result[params_current[0]] = typeof params_current[1] === 'undefined' ? '' : params_current[1]
    }
    const utm_source = !params_result['utm_source'] ? '' : 'utm_source=' + params_result['utm_source']
    const utm_medium = !params_result['utm_medium'] ? '' : '&utm_medium=' + params_result['utm_medium']
    const utm_campaign = !params_result['utm_campaign'] ? '' : '&utm_campaign=' + params_result['utm_campaign']
    const utm_term = !params_result['utm_term'] ? '' : '&utm_term=' + params_result['utm_term']
    const utm_content = !params_result['utm_content'] ? '' : '&utm_content=' + params_result['utm_content']
    const l_search = utm_source + utm_medium + utm_campaign + utm_term + utm_content

    getDataLayerPush()?.({
      originalLocation: l_search,
    })
  }

  private async configureAmplitude() {
    const state = this.store.getState()
    const { isPolygon, amplitude: amplitudeKey, appRequestId } = configSelector(state)
    if (isPolygon) return

    const testFlights = await this.store.dispatch(resolveThunkSelector(testFlightsResultSelector))

    void this.amplitude.init(amplitudeKey, appRequestId)

    const user = profileUserSelector(this.store.getState())

    const identify = (user: AnalyticsUser | null) => {
      analyticsIdentify(this.amplitude, user)
      errorReport.identify(user, { supervised: user?.supervised ?? false })
      if (user) this.reportYmClientId()
    }

    const analyticsUser: AnalyticsUser | null = user ? { ...user, test_flights: testFlights, supervised: isSupervisedSelector(this.store.getState()) } : null
    identify(analyticsUser)

    let metricsUser = user

    this.store.subscribe(() => {
      const prevUser = metricsUser
      metricsUser = profileUserSelector(this.store.getState())

      if (prevUser === metricsUser) return

      if (metricsUser && metricsUser.id && prevUser?.id !== metricsUser.id) {
        const analyticsUser: AnalyticsUser | null = metricsUser
          ? { ...metricsUser, test_flights: testFlights, supervised: isSupervisedSelector(this.store.getState()) }
          : null
        identify(analyticsUser)
      }
    })
  }

  /** Reports Metrika ClientID to backend so offline conversions can be linked by ClientId */
  private reportYmClientId() {
    const send = async () => {
      const uid = await this.store.dispatch(getCookies()).get('_ym_uid')
      if (!uid) return false

      const sentKey = 'kidsout__ym_client_id_sent'
      if (tryOr(() => window.sessionStorage.getItem(sentKey), null) === uid) return true

      this.store.dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // Metrika's client id, recorded once a session so a visit can be joined to its reports.
          type: 'analytics.ym_client_id',
          data: { ym_client_id: uid },
        })).set('trackings')
      )
      tryOr(() => window.sessionStorage.setItem(sentKey, uid), undefined)
      return true
    }

    void send().then(sent => {
      if (!sent) {
        // on the very first visit _ym_uid cookie appears only after consent + tag.js init
        window.setTimeout(() => void send(), 30_000)
      }
    })
  }

  private configureAnalytics() {
    const state = this.store.getState()
    const { appProtocol } = configSelector(state)
    const inWebview = appProtocol > 1

    this.store.dispatch(
      setAnalyticsRouter(
        createAnalyticsRouter({
          trackings: new TrackingsService(this.store),
          amplitude: this.amplitude,
          getBridge: inWebview ? () => this.store.dispatch(getApplicationBridge()) : undefined,
        })
      )
    )
  }

  private handleOnRouterReady = (router: RouterContextInterface) => {
    this.store.dispatch(setRouter(router))
  }

  private handleAfterRender: RouterInterfaceAfterRenderCallback = async ({ matchedRoute, ...router }) => {
    console.info('[RENDERED]', `${new Date().getTime() - this.browserTimestamp}ms`)

    const state = this.store.getState()
    const { title } = metaSelector(state)

    const url = location.pathname + location.search + location.hash

    const extractor = new RouteDataExtractor(router)
    const data = await extractor.extract(matchedRoute)
    this.store.dispatch(getAnalyticsRouter()).send(
      analyticsPageview(() => ({
        // A page was rendered. The only pageview on the web, one per navigation.
        type: data.event_id,
        url,
        path: location.pathname,
        title,
        data: data.event_data,
      })).set('all')
    )
  }
}

const waitIdle = () =>
  new Promise<void>(resolve =>
    (window.requestIdleCallback ?? setTimeout)(() => {
      resolve()
    })
  )
